import { FreeModeLogo } from '@repo/freemode-config/logo'
import { AIStudioLogo } from '@repo/studio-design-system-ui-ai-studio-logo'
import { AIStudioVisualMark } from '@repo/studio-design-system-ui-ai-studio-logo/visual-mark'
import { MarketingStudioLogo } from '@repo/studio-design-system-ui-marketing-studio-logo'
import { MarketingStudioVisualMark } from '@repo/studio-design-system-ui-marketing-studio-logo/visual-mark'
import { StudioUnlimitedLogo } from '@repo/studio-design-system-ui-studio-unlimited-logo'
import { InfinityIcon } from '@repo/studio-design-system-ui-icons/arrows/infinity'
import { Dataflow01Icon } from '@repo/studio-design-system-ui-icons/development/dataflow-01'
import { TerminalIcon } from '@repo/studio-design-system-ui-icons/development/terminal'
import { Monitor01Icon } from '@repo/studio-design-system-ui-icons/media-devices/monitor-01'
import { Cube03Icon } from '@repo/studio-design-system-ui-icons/shapes/cube-03'
import { Star04Icon } from '@repo/studio-design-system-ui-icons/shapes/star-04'
import { Surface } from '@repo/rpc-types-creativefabrica-common-v1/surface/v1'
import { TOOL_ICONS } from '@repo/studio-tool-config/tool-icons'
import { ROUTES } from '@repo/studio-utils/routes'
import type { StudioWorkspaceId } from '@repo/studio-config/studio-config-context'

type WorkspaceDescriptionKey =
  | 'studio.workspaces.studio_ai.description'
  | 'studio.workspaces.marketing_studio.description'
  | 'studio.workspaces.freemode.description'
  | 'studio.workspaces.unlimited.description'
  | 'studio.workspaces.3d.description'
  | 'studio.workspaces.workflows.description'
  | 'studio.workspaces.platform.description'
  | 'studio.workspaces.desktop.description'
  | 'studio.workspaces.lumo_canvas.description'

/**
 * A Studio surface with its own branding and home, optionally its own sidebar.
 * Adding one means an entry here plus a route subtree wrapped in
 * `StudioWorkspaceProvider`; existing workspaces are untouched.
 */
export interface StudioWorkspace {
  id: StudioWorkspaceId
  label: string
  descriptionKey: WorkspaceDescriptionKey
  /** Wordmark, for the top bar trigger. */
  logo: React.ReactNode
  /** Compact mark, for the switcher menu row. */
  visualMark: React.ReactNode
  homeRoute: string
  /**
   * Whether every switcher offers it. An unannounced workspace is still
   * listed while you are in it, so it can always name itself.
   */
  announced: boolean
  /**
   * Full document load instead of SPA navigate. Use when another microfrontend
   * owns the home URL — client routing stays inside Studio and 404s.
   */
  hardNavigation?: boolean
  surface?: Surface
}

export const STUDIO_AI_WORKSPACE: StudioWorkspace = {
  id: 'studio-ai',
  label: 'idea.ai',
  descriptionKey: 'studio.workspaces.studio_ai.description',
  logo: <AIStudioLogo className="h-4 w-auto" />,
  visualMark: <AIStudioVisualMark />,
  homeRoute: ROUTES.STUDIO.HOME,
  announced: true,
}

export const MARKETING_STUDIO_WORKSPACE: StudioWorkspace = {
  id: 'marketing-studio',
  label: 'Marketing',
  descriptionKey: 'studio.workspaces.marketing_studio.description',
  logo: <MarketingStudioLogo className="h-6 w-auto" />,
  visualMark: <MarketingStudioVisualMark />,
  homeRoute: ROUTES.MARKETING_STUDIO.HOME,
  announced: true,
}

export const FREEMODE_WORKSPACE: StudioWorkspace = {
  id: 'freemode',
  label: 'FreeMode',
  descriptionKey: 'studio.workspaces.freemode.description',
  logo: <FreeModeLogo className="h-4 w-auto" />,
  visualMark: <Star04Icon />,
  homeRoute: ROUTES.FREEMODE.HOME,
  announced: false,
}

export const UNLIMITED_WORKSPACE: StudioWorkspace = {
  id: 'unlimited',
  label: 'Unlimited',
  descriptionKey: 'studio.workspaces.unlimited.description',
  logo: <StudioUnlimitedLogo className="h-[13.222px] w-[80.703px] shrink-0" />,
  visualMark: <InfinityIcon />,
  homeRoute: ROUTES.UNLIMITED.HOME,
  announced: true,
  surface: Surface.UNLIMITED,
}

export const THREED_WORKSPACE: StudioWorkspace = {
  id: '3d',
  label: '3D',
  descriptionKey: 'studio.workspaces.3d.description',
  logo: (
    <span className="inline-flex items-center gap-1.5 studio-typography-16">
      <Cube03Icon className="size-5" />
      3D
    </span>
  ),
  visualMark: <Cube03Icon />,
  homeRoute: ROUTES.THREED_WORKSPACE.HOME,
  announced: true,
}

export const PLATFORM_WORKSPACE: StudioWorkspace = {
  id: 'platform',
  label: 'Platform API',
  descriptionKey: 'studio.workspaces.platform.description',
  logo: (
    <span className="inline-flex items-center gap-1.5 studio-typography-16">
      <TerminalIcon className="size-5" />
      Platform API
    </span>
  ),
  visualMark: <TerminalIcon />,
  homeRoute: ROUTES.PLATFORM.ROOT,
  announced: true,
  hardNavigation: true,
}

export const DESKTOP_WORKSPACE: StudioWorkspace = {
  id: 'desktop',
  label: 'Desktop',
  descriptionKey: 'studio.workspaces.desktop.description',
  logo: (
    <span className="inline-flex items-center gap-1.5 studio-typography-16">
      <Monitor01Icon className="size-5" />
      Desktop
    </span>
  ),
  visualMark: <Monitor01Icon />,
  homeRoute: ROUTES.DESKTOP.ROOT,
  announced: true,
  hardNavigation: true,
}

export const WORKFLOWS_WORKSPACE: StudioWorkspace = {
  id: 'workflows',
  label: 'Workflows',
  descriptionKey: 'studio.workspaces.workflows.description',
  // Design has drawn no Workflows wordmark, so its own tab glyph stands in beside the name.
  logo: (
    <span className="inline-flex items-center gap-1.5 studio-typography-16">
      <Dataflow01Icon className="size-5" />
      Workflows
    </span>
  ),
  visualMark: <Dataflow01Icon />,
  homeRoute: ROUTES.WORKFLOWS.ROOT,
  announced: true,
}

export const LUMO_CANVAS_WORKSPACE: StudioWorkspace = {
  id: 'lumo-canvas',
  label: 'Lumo Canvas',
  descriptionKey: 'studio.workspaces.lumo_canvas.description',
  logo: <span className="studio-typography-16">Lumo Canvas</span>,
  visualMark: TOOL_ICONS.TOOL_TYPE_LUMO,
  homeRoute: ROUTES.CANVAS.ROOT,
  announced: true,
  hardNavigation: true,
}

export const STUDIO_WORKSPACES: StudioWorkspace[] = [
  STUDIO_AI_WORKSPACE,
  WORKFLOWS_WORKSPACE,
  MARKETING_STUDIO_WORKSPACE,
  DESKTOP_WORKSPACE,
  THREED_WORKSPACE,
  PLATFORM_WORKSPACE,
  FREEMODE_WORKSPACE,
  UNLIMITED_WORKSPACE,
  LUMO_CANVAS_WORKSPACE,
]

/** Announced workspaces, plus the current one so a hidden route can still name itself. */
export function availableWorkspaces(current: StudioWorkspace): StudioWorkspace[] {
  return STUDIO_WORKSPACES.filter(workspace => workspace.announced || workspace === current)
}
